import { Grid } from "@mui/material"
import { Controller, useForm } from "react-hook-form"
import type { AppNode } from "@features/builder/graph/types"
import type { CreateEdgesFormProps } from "@features/builder/CreateEdges/types"
import NodeSelectRow from "@features/builder/CreateEdges/NodeSelectRow"
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { MAX_SELECTED_NODES } from "@features/builder/CreateEdges/helpers"
import { useState } from "react"
import { createEdgesValidationSchema } from "@features/builder/CreateEdges/validation"

type FormValues = yup.InferType<typeof createEdgesValidationSchema>

const filterSelectedNodesOptions = (targetNodes: AppNode[], nodeOptions: AppNode[]) => {
  const targetNodesIds = new Set(targetNodes.map((node) => node.id))
  return nodeOptions.filter((node) => !targetNodesIds.has(node.id))
}

const filterTargetNodesOptions = (selectedNodes: AppNode[], nodeOptions: AppNode[]) => {
  const selectedNodesIds = new Set(selectedNodes.map((node) => node.id))
  return nodeOptions.filter((node) => !selectedNodesIds.has(node.id))
}

export default function CreateEdgesForm({
  initialSourceNodes,
  allNodes,
  onPreview,
  initialDirection,
}: CreateEdgesFormProps<FormValues>) {
  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      direction: initialDirection,
      selectedNodes: initialSourceNodes.slice(0, MAX_SELECTED_NODES),
      targetNodes: [],
    },
    resolver: yupResolver(createEdgesValidationSchema),
  })

  const [selectedNodesOptions, setSelectedNodesOptions] = useState<AppNode[]>(() =>
    filterSelectedNodesOptions([], allNodes),
  )
  const [targetNodesOptions, setTargetNodesOptions] = useState<AppNode[]>(() =>
    filterTargetNodesOptions(initialSourceNodes, allNodes),
  )

  const direction = watch("direction")
  const oppositeDirection = direction === "Parents" ? "Children" : "Parents"

  const handleSelectedNodesChange = (selectedNodes: AppNode[]) => {
    setTargetNodesOptions(filterTargetNodesOptions(selectedNodes, allNodes))
  }

  const handleTargetNodesChange = (targetNodes: AppNode[]) => {
    setSelectedNodesOptions(filterSelectedNodesOptions(targetNodes, allNodes))
  }

  return (
    <form id="create-edges-form" onSubmit={handleSubmit(onPreview)}>
      <Grid container spacing={2} alignItems="center">
        <Controller
          name="selectedNodes"
          control={control}
          render={({ field, fieldState }) => (
            <NodeSelectRow
              direction={direction}
              onDirectionChange={(d) => setValue("direction", d)}
              options={selectedNodesOptions}
              value={field.value}
              onChange={(nodes) => {
                field.onChange(nodes)
                handleSelectedNodesChange(nodes)
              }}
              label="Selected nodes"
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="targetNodes"
          control={control}
          render={({ field, fieldState }) => (
            <NodeSelectRow
              direction={oppositeDirection}
              onDirectionChange={(picked) =>
                setValue("direction", picked === "Parents" ? "Children" : "Parents")
              }
              options={targetNodesOptions}
              value={field.value}
              onChange={(nodes) => {
                field.onChange(nodes)
                handleTargetNodesChange(nodes)
              }}
              label="Target nodes"
              error={fieldState.error?.message}
            />
          )}
        />
      </Grid>
    </form>
  )
}
